import { PRIORITIES, TYPE_LABELS, type Priority, type TicketType } from '@lixionary-jira/domain';
import { Archive, Bug, CircleAlert, CircleHelp, Globe, Lightbulb, OctagonAlert, UserX } from 'lucide-react';
import type { ExternalReporter } from '../../api/types';
import { Badge } from '../../ui/Badge';

const PRIORITY_TONE: Record<Priority, 'default' | 'info' | 'warning' | 'error'> = {
  low: 'default',
  normal: 'default',
  high: 'warning',
  urgent: 'error',
};
export const PRIORITY_LABEL: Record<Priority, string> = {
  low: 'Low',
  normal: 'Normal',
  high: 'High',
  urgent: 'Urgent',
};
export const PRIORITY_OPTIONS = PRIORITIES.map((p) => ({ value: p, label: PRIORITY_LABEL[p] }));

export const PriorityBadge = ({ priority }: { priority: Priority }) => (
  <Badge tone={PRIORITY_TONE[priority]} dot={false}>
    {PRIORITY_LABEL[priority]}
  </Badge>
);

/** "Jane Doe via iam-v2": the person behind a ticket another platform's service account submitted. */
export function ExternalReporterLabel({
  reporter,
  withEmail = false,
}: {
  reporter: ExternalReporter;
  withEmail?: boolean;
}) {
  return (
    <span className="row nowrap" style={{ gap: 4 }} title={`Reported through ${reporter.source}`}>
      <Globe size={12} aria-hidden />
      {reporter.displayName}
      {withEmail && reporter.email && <span className="muted"> ({reporter.email})</span>}
      <span className="muted"> via {reporter.source}</span>
    </span>
  );
}

export function TypeLabel({ type }: { type: TicketType }) {
  const Icon = type === 'issue' ? Bug : Lightbulb;
  return (
    <span className="row nowrap small" style={{ gap: 4 }}>
      <Icon size={13} aria-hidden />
      {TYPE_LABELS[type]}
    </span>
  );
}

/** Indicators are text + icon, never color alone, and never extra board columns (WF-01). */
export function TicketFlags({
  t,
}: {
  t: {
    blocked: boolean;
    blockedReasons?: string[];
    needsClarification: boolean;
    staleOwner: boolean;
    deliveryFailure: boolean;
    archived: boolean;
  };
}) {
  return (
    <>
      {t.blocked && (
        <span className="flag error" title={t.blockedReasons?.join('; ')}>
          <OctagonAlert size={13} aria-hidden /> Blocked
        </span>
      )}
      {t.needsClarification && (
        <span className="flag warning">
          <CircleHelp size={13} aria-hidden /> Needs clarification
        </span>
      )}
      {t.staleOwner && (
        <span className="flag error">
          <UserX size={13} aria-hidden /> Stale owner
        </span>
      )}
      {t.deliveryFailure && (
        <span className="flag error">
          <CircleAlert size={13} aria-hidden /> Delivery failed or unknown
        </span>
      )}
      {t.archived && (
        <span className="flag info">
          <Archive size={13} aria-hidden /> Archived
        </span>
      )}
    </>
  );
}

export const formatBytes = (n: number) =>
  n < 1024 ? `${n} B` : n < 1048576 ? `${(n / 1024).toFixed(1)} KiB` : `${(n / 1048576).toFixed(1)} MiB`;
